import React, { ChangeEvent, useState } from "react";
import { Container, ConnectedAlert, ProgressBar, RadioButtonWithLabel } from "../../../../components";
import { Checkbox, Icon, Text, Heading, Button, TextField, Link } from "@telia/teddy";
import { connect, useDispatch } from "react-redux";
import { useParams } from "react-router";
import { RootState } from "../../../../../../models";
import { RejectReason, RejectReasonID } from "../../../../../../models/transfer";
import {
    isOrderNotFound as isOrderNotFoundSelector,
    isOrgTheOrderer as isOrgTheOrdererSelector,
} from "../../../../app/state/selectors";
import { PhoneNumberFormatters } from "../../../../app/utils";
import { isBankIdVerified as isFormerLegalBankIdVerified } from "../../../../authentication/state/selectors";
import BankIdSection from "../../../../checkout/components/molecules/BankIdSectionTeliaBroker/BankIdSectionTeliaBroker";
// TODO: Swap with new consent endpoint from OM
import { submitConsent, rejectConsent } from "../../../../checkout/state/actions/transferOwnershipActions";
import {
    getHasSubmittedTransferOrder,
    getHasRejectedTransferOrder,
    getSelectedPhoneNumber,
    getTransferExistingIndividualLegalOwnerFullName,
} from "../../../../checkout/state/selectors/accountSelectors";
import { getLegalOwnerFullName } from "../../../../checkout/state/selectors/customerInfoSelectors";
import { selectors as LOADING_SELECTORS } from "../../../../loading";
import TEXT from "../../../text-consts";

import "./LandingPage.less";

const formatter = PhoneNumberFormatters[0].displayFn;

interface StateProps {
    existingLegalOwnerFullName: string;
    hasRejectedTransferOrder: boolean;
    hasSubmittedTransferOrder: boolean;
    isBankIdVerified: boolean;
    isFetchingData: boolean;
    isOrderNotFound: boolean;
    isOrgTheOrderer: boolean;
    legalOwnerFullName: string;
    selectedPhoneNumber: string;
}

type LandingPageFromCheckoutProps = StateProps;

const LandingPageFromCheckout: React.FC<LandingPageFromCheckoutProps> = ({
    // Redux (data)
    isBankIdVerified,
    isFetchingData,
    isOrderNotFound,
    isOrgTheOrderer,
    hasSubmittedTransferOrder,
    hasRejectedTransferOrder,
    selectedPhoneNumber,
    existingLegalOwnerFullName,
    legalOwnerFullName,
}) => {
    const dispatch = useDispatch();

    const { orderGuid } = useParams() as { orderGuid: string };
    const [isSubmitted, setIsSubmitted] = useState(hasSubmittedTransferOrder);
    const [isRejected, setIsRejected] = useState(hasRejectedTransferOrder);
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [showRejectReasonInput, setRejectReasonInput] = useState(false);
    const [hasClickedRejected, setHasClickedRejected] = useState(false);
    const [selectedOption, setSelectedOption] = useState<"accept" | "reject">("accept");
    const [rejectReasons, setRejectReasons] = useState<RejectReasonID[]>([]);
    const referer = "consentFormerLegalTransferB2C";

    const reasons: RejectReason[] = [
        { id: "NOT_OWNER", text: "Jeg er ikke eier av dette nummeret" },
        { id: "NOT_FAMILIAR_NUMBER", text: "Jeg kjenner ikke til at dette nummeret skal overføres" },
        { id: "OTHER", text: "Annet", reason: rejectReason },
    ];

    const handleReasonChange = (e: ChangeEvent<HTMLInputElement>) => {
        setRejectReason((e.target as HTMLInputElement).value);
    };

    async function handleSubmitTransferOwnershipOrder() {
        setHasClickedSubmit(true);
        await dispatch(submitConsent(orderGuid));
        setIsSubmitted(true);
    }

    async function handleRejectTransferOwnershipOrder() {
        setHasClickedRejected(true);
        const texts: string[] = [];
        rejectReasons.forEach((id) => {
            const reason = reasons.find((r) => r.id === id);
            if (reason?.reason) {
                texts.push(reason.reason);
            } else if (reason?.text) {
                texts.push(reason.text);
            }
        });
        await dispatch(rejectConsent(orderGuid, texts));
        setIsRejected(true);
    }

    const handleRejectReasons = (id: RejectReasonID) => {
        if (id === "OTHER") {
            setRejectReasonInput(!showRejectReasonInput);
        }
        if (rejectReasons.includes(id)) {
            setRejectReasons(rejectReasons.filter((reason) => reason !== id));
        } else {
            setRejectReasons([...rejectReasons, id]);
        }
    };

    if (isSubmitted) {
        return (
            <div className="tlo-landing-page">
                <div className="tlo-landing-page__heading-container">
                    <Heading mb="100" as="h1" variant="title-600">
                        {TEXT.LANDING_PAGE_FROM_CHECKOUT.CONFIRM_HEADING}
                    </Heading>
                </div>
                <Container size="medium" className="tlo-landing-page__confirmation-container">
                    <span>
                        <Icon name="check-circle-filled" className="tlo-landing-page__confirmation-icon" />
                    </span>
                    <div>
                        <div
                            className="paragraph paragraph--fineprint"
                            dangerouslySetInnerHTML={{
                                __html: TEXT.LANDING_PAGE_FROM_CHECKOUT.ORDER_CONFIRMATION(legalOwnerFullName),
                            }}
                            data-di-mask
                        />
                        <Link variant="standalone" href="https://www.telia.no/">
                            {TEXT.LANDING_PAGE_FROM_CHECKOUT.BACK_TO_FRONTPAGE}
                        </Link>
                    </div>
                </Container>
            </div>
        );
    }

    if (isRejected) {
        return (
            <div className="tlo-landing-page">
                <div className="tlo-landing-page__heading-container">
                    <Heading mb="100" as="h1" variant="title-600">
                        {TEXT.LANDING_PAGE_FROM_CHECKOUT.REJECT_HEADING}
                    </Heading>
                </div>
                <Container size="medium" className="tlo-landing-page__confirmation-container">
                    <span>
                        <Icon name="close" className="tlo-landing-page__reject-icon" />
                    </span>
                    <div>
                        <div
                            className="paragraph paragraph--fineprint"
                            dangerouslySetInnerHTML={{
                                __html: TEXT.LANDING_PAGE_FROM_CHECKOUT.ORDER_REJECTED(legalOwnerFullName),
                            }}
                            data-di-mask
                        />
                        <Link variant="standalone" href="https://www.telia.no/">
                            {TEXT.LANDING_PAGE_FROM_CHECKOUT.BACK_TO_FRONTPAGE}
                        </Link>
                    </div>
                </Container>
            </div>
        );
    }

    if (isOrgTheOrderer) {
        return (
            <div className="tlo-landing-page">
                <div className="tlo-landing-page__heading-container">
                    <Heading mb="100" as="h1" variant="title-600">
                        {TEXT.LANDING_PAGE_FROM_CHECKOUT.HEADING}
                    </Heading>
                </div>
                <Container size="medium" className="tlo-landing-page__confirmation-container">
                    <div>
                        <Text mb="100">{TEXT.LANDING_PAGE_FROM_CHECKOUT.ORDERER_IS_ORGANIZATION}</Text>
                        <Link variant="standalone" href="https://www.telia.no/">
                            {TEXT.LANDING_PAGE_FROM_CHECKOUT.BACK_TO_FRONTPAGE}
                        </Link>
                    </div>
                </Container>
            </div>
        );
    }

    if (isOrderNotFound) {
        return (
            <div className="tlo-landing-page">
                <div className="tlo-landing-page__heading-container">
                    <Heading mb="100" as="h1" variant="title-600">
                        {TEXT.LANDING_PAGE_FROM_CHECKOUT.HEADING}
                    </Heading>
                </div>
                <Container size="medium" className="tlo-landing-page__confirmation-container">
                    <div>
                        <Text mb="100">{TEXT.LANDING_PAGE_FROM_CHECKOUT.ORDER_NOT_FOUND}</Text>
                        <Link variant="standalone" href="https://www.telia.no/">
                            {TEXT.LANDING_PAGE_FROM_CHECKOUT.BACK_TO_FRONTPAGE}
                        </Link>
                    </div>
                </Container>
            </div>
        );
    }

    return (
        <div className="tlo-landing-page">
            <div className="tlo-landing-page__heading-container">
                <Heading mb="100" as="h1" variant="title-600">
                    {TEXT.LANDING_PAGE_FROM_CHECKOUT.HEADING}
                </Heading>
            </div>
            <ProgressBar isFetchingData={isFetchingData} standByColor="light-grey" />
            <ConnectedAlert />
            <Container size="medium" noMargin>
                <div
                    className="paragraph paragraph--fineprint"
                    dangerouslySetInnerHTML={{
                        __html: TEXT.LANDING_PAGE_FROM_CHECKOUT.LIST.DESCRIPTION_1(
                            legalOwnerFullName,
                            selectedPhoneNumber,
                        ),
                    }}
                    data-di-mask
                />
                <Text mb="100" variant="additional-100">
                    {TEXT.LANDING_PAGE_FROM_CHECKOUT.LIST.DESCRIPTION_2}
                </Text>
                <Heading mb="100" as="h2" variant="title-200">
                    {"Hva må du gjøre?"}
                </Heading>
                <Text mb="100" variant="additional-100">
                    {TEXT.LANDING_PAGE_FROM_CHECKOUT.LIST.DESCRIPTION_3}
                </Text>
            </Container>
            <Container size="medium" whiteBg className="tlo-landing-page__section">
                <div className="tlo-landing-page__consent-option">
                    <RadioButtonWithLabel
                        value="accept"
                        onChange={(event) => {
                            setSelectedOption(event.target.value as "accept" | "reject");
                        }}
                        name="tlo-from-checkout"
                        checked={selectedOption === "accept"}
                        hasRichContent
                    >
                        <Heading
                            mb="100"
                            as="h3"
                            variant="title-100"
                            className="tlo-landing-page__consent-radio-heading"
                        >
                            {"Jeg godkjenner overføringen"}
                        </Heading>
                    </RadioButtonWithLabel>
                    {!isBankIdVerified && selectedOption === "accept" && (
                        <div className="tlo-landing-page__consent-expanded">
                            <Text mb="100" variant="additional-100">
                                {TEXT.LANDING_PAGE_FROM_CHECKOUT.BANK_ID_CONSENT}
                            </Text>
                            <div className="tlo-landing-page__bankid-container">
                                <BankIdSection
                                    referer={referer}
                                    skipDisplayHeading={true}
                                    skipDisplayDescription={true}
                                />
                            </div>
                        </div>
                    )}
                    {selectedOption === "accept" && isBankIdVerified && (
                        <div className="tlo-landing-page__consent-expanded">
                            <Text mb="100" variant="additional-100">
                                {TEXT.LANDING_PAGE_FROM_CHECKOUT.BANK_ID_VERIFIED}
                            </Text>
                            <div className="tlo-landing-page__verified-user">
                                <Text mb="100" variant="additional-100" className="telia-sans-medium" data-di-mask>
                                    {existingLegalOwnerFullName}
                                </Text>
                            </div>
                        </div>
                    )}
                </div>
                <div className="tlo-landing-page__consent-option">
                    <RadioButtonWithLabel
                        value="reject"
                        onChange={(event) => {
                            setSelectedOption(event.target.value as "accept" | "reject");
                        }}
                        name="tlo-from-checkout"
                        checked={selectedOption === "reject"}
                        hasRichContent
                    >
                        <Heading
                            mb="100"
                            as="h3"
                            variant="title-100"
                            className="tlo-landing-page__consent-radio-heading"
                        >
                            {"Jeg avslår overføringen"}
                        </Heading>
                    </RadioButtonWithLabel>
                    {selectedOption === "reject" && (
                        <div className="tlo-landing-page__consent-expanded">
                            <Text mb="100" variant="additional-100">
                                {TEXT.LANDING_PAGE_FROM_CHECKOUT.REJECT_DESCRIPTION}
                            </Text>
                            <div className="tlo-landing-page__consent-reject-container">
                                {reasons.map((reason) => (
                                    <div key={reason.id} className="tlo-landing-page__consent-reject-option">
                                        <Checkbox>
                                            <Checkbox.Trigger
                                                checked={rejectReasons.includes(reason.id)}
                                                onCheckedChange={() => handleRejectReasons(reason.id)}
                                            />
                                            <span>{reason.text}</span>
                                        </Checkbox>
                                        {showRejectReasonInput && reason.reason !== undefined && (
                                            <TextField>
                                                <TextField.Input
                                                    placeholder="Beskriv kort årsaken"
                                                    value={rejectReason}
                                                    onChange={handleReasonChange}
                                                    data-di-mask
                                                />
                                            </TextField>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </Container>
            <Container size="medium" className="tlo-landing-page__right-align">
                {selectedOption === "accept" && (
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={!isBankIdVerified || hasClickedSubmit}
                        onClick={handleSubmitTransferOwnershipOrder}
                    >
                        {TEXT.LANDING_PAGE_FROM_CHECKOUT.BUTTON.CONFIRM_TRANSFER}
                    </Button>
                )}
                {selectedOption === "reject" && (
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={hasClickedRejected}
                        onClick={handleRejectTransferOwnershipOrder}
                    >
                        {TEXT.LANDING_PAGE_FROM_CHECKOUT.BUTTON.REJECT}
                    </Button>
                )}
            </Container>
        </div>
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => ({
    isBankIdVerified: isFormerLegalBankIdVerified(state),
    isFetchingData: LOADING_SELECTORS.isLoading(state),
    isOrderNotFound: isOrderNotFoundSelector(state),
    isOrgTheOrderer: isOrgTheOrdererSelector(state),
    hasSubmittedTransferOrder: getHasSubmittedTransferOrder(state),
    hasRejectedTransferOrder: getHasRejectedTransferOrder(state),
    selectedPhoneNumber: formatter(getSelectedPhoneNumber(state)),
    existingLegalOwnerFullName: getTransferExistingIndividualLegalOwnerFullName(state) || "",
    legalOwnerFullName: getLegalOwnerFullName(state),
});

export default connect(mapStateToProps)(LandingPageFromCheckout);
